import { useEffect, useState } from "react";
import { brand, navLinks } from "@/mocks/baoliaoData";

interface TopNavProps {
  search: string;
  onSearchChange: (v: string) => void;
  onSearchActive: () => void;
}

export default function TopNav({ search, onSearchChange, onSearchActive }: TopNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusBox, setFocusBox] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (v: string) => {
    onSearchChange(v);
    if (v.trim()) onSearchActive();
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/80 shadow-[0_1px_0_0_rgba(0,0,0,0.02)]" : "bg-background-50"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between gap-4 h-16 md:h-[68px]">
          {/* brand */}
          <a href="#headline" className="flex items-center gap-2 whitespace-nowrap group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-flashlight-fill text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-xl md:text-2xl tracking-tight text-foreground-950">
                {brand}
                <span className="text-primary-500">.</span>
              </span>
              <span className="text-[10px] tracking-widest text-foreground-500 mt-1">全网热瓜 实时追踪</span>
            </span>
          </a>

          {/* desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3.5 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* actions */}
          <div className="flex items-center gap-2">
            <div
              className={`hidden md:flex items-center gap-2 rounded-full border transition-all ${
                focusBox || search
                  ? "border-primary-300 bg-background-50 ring-1 ring-primary-200"
                  : "border-background-300 bg-background-100/70"
              } px-3.5 py-2`}
            >
              <i className={`ri-search-line text-sm ${focusBox ? "text-primary-500" : "text-foreground-500"}`}></i>
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => setFocusBox(true)}
                onBlur={() => setFocusBox(false)}
                placeholder="搜实时爆料 / 热瓜…"
                className="bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 w-36 xl:w-44"
                aria-label="搜索实时爆料"
              />
            </div>
            <a
              href="#subscribe"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-foreground-950 text-background-50 px-4 py-2 text-sm font-semibold hover:bg-primary-600 hover:text-background-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-notification-3-line text-sm"></i>
              实时订阅
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 bg-background-50 text-foreground-900"
              aria-label="展开菜单"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {/* mobile panel */}
      {open && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-rise">
          <div className="max-w-[1240px] mx-auto px-4 md:px-6 py-3 flex flex-col gap-1">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100/70 px-3.5 py-2 mb-2">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={search}
                onChange={(e) => {
                  onSearchChange(e.target.value);
                  if (e.target.value.trim()) onSearchActive();
                }}
                placeholder="搜实时爆料 / 热瓜…"
                className="bg-transparent outline-none text-sm text-foreground-900 w-full"
                aria-label="搜索实时爆料"
              />
            </div>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#subscribe"
              onClick={() => setOpen(false)}
              className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-semibold"
            >
              <i className="ri-notification-3-line text-sm"></i>
              实时订阅
            </a>
          </div>
        </div>
      )}
    </header>
  );
}